import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';

// Target: 12. September 2026, 11:00 Berlin time
// Using ISO with explicit +02:00 (CEST) — Sept 12 is still summer time in Germany
const TARGET_ISO = '2026-09-12T11:00:00+02:00';

const calcRemaining = () => {
  const now = new Date();
  const target = new Date(TARGET_ISO);
  const diff = target.getTime() - now.getTime();
  if (diff <= 0) return null;
  return {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((diff / (1000 * 60)) % 60),
    seconds: Math.floor((diff / 1000) % 60)
  };
};

const pad = (n) => String(n).padStart(2, '0');

const Unit = ({ value, label, testId }) => (
  <div data-testid={testId} className="flex flex-col items-center min-w-[60px] sm:min-w-[80px] md:min-w-[100px]">
    <div className="relative">
      <span
        className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-white tabular-nums leading-none"
        style={{ fontFamily: "'Cormorant', serif", textShadow: '0 2px 20px rgba(0,0,0,0.6)' }}
      >
        {pad(value)}
      </span>
    </div>
    <span className="mt-2 md:mt-3 text-[9px] sm:text-[10px] md:text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-light" style={{ textShadow: '0 2px 12px rgba(0,0,0,0.8)' }}>
      {label}
    </span>
  </div>
);

const Separator = () => (
  <span
    className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-[#D4AF37]/80 leading-none pb-6 md:pb-7"
    style={{ fontFamily: "'Cormorant', serif", textShadow: '0 2px 15px rgba(0,0,0,0.7)' }}
    aria-hidden="true"
  >
    :
  </span>
);

const Countdown = () => {
  const [remaining, setRemaining] = useState(calcRemaining());

  useEffect(() => {
    const t = setInterval(() => setRemaining(calcRemaining()), 1000);
    return () => clearInterval(t);
  }, []);

  // After the event date, show elegant message
  if (!remaining) {
    return (
      <motion.div
        data-testid="countdown-past"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        className="text-center"
      >
        <p className="text-[#D4AF37] text-xs sm:text-sm uppercase tracking-[0.3em] font-light">
          Besichtigung findet heute statt
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      data-testid="countdown-timer"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col items-center"
    >
      <p className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#F4E4C1]/90 mb-4 md:mb-6 font-light" style={{ textShadow: '0 2px 15px rgba(0,0,0,0.8)' }}>
        Besichtigung in
      </p>
      <div className="flex items-end justify-center gap-1 sm:gap-3 md:gap-5">
        <Unit value={remaining.days} label="Tage" testId="cd-days" />
        <Separator />
        <Unit value={remaining.hours} label="Stunden" testId="cd-hours" />
        <Separator />
        <Unit value={remaining.minutes} label="Minuten" testId="cd-minutes" />
        <Separator />
        <Unit value={remaining.seconds} label="Sekunden" testId="cd-seconds" />
      </div>
      <div className="h-px w-32 md:w-48 mt-6 md:mt-8 bg-gradient-to-r from-transparent via-[#D4AF37]/40 to-transparent" />
    </motion.div>
  );
};

export default Countdown;
